"use client";

import { useState, useEffect } from "react";
import { type AppContext } from "@/types";
import { useTranslation } from "react-i18next";
import Modal from "@/shared/ui/components/modal";
import List, { ListItem } from "@/shared/ui/components/list";
import { CartItemIcon } from "@/shared/ui/icons/cart-item-icon";
import MoneyAmount from "@/shared/ui/components/money-amount";
import { preloadImage } from "@/lib/icons";

interface CartModalProps {
  ctx: AppContext;
  isOpen: boolean;
  onClose: () => void;
}

export function CartModal({
  isOpen,
  onClose,
  ctx: {
    session: {
      merchant,
      order: { items: orderItems, currency },
    },
  },
}: CartModalProps) {
  const { t, i18n } = useTranslation();
  const [showLogo, setShowLogo] = useState(false);

  useEffect(() => {
    if (merchant.logoUrl) {
      preloadImage(
        merchant.logoUrl,
        () => setShowLogo(true),
        () => setShowLogo(false),
      );
    }
  }, [merchant.logoUrl]);

  if (!isOpen) return null;

  const subtotal = orderItems.reduce(
    (total, item) => total + item.totalAmount.amount * item.quantity,
    0,
  );

  return (
    <Modal onClose={onClose} showCloseButton={true}>
      <div className="p-6 pb-0">
        <div className="flex items-center justify-start mb-6 gap-3 pt-2">
          {showLogo && (
            <img
              src={merchant.logoUrl}
              style={{ width: 48, height: 48 }}
              alt="Merchant logo"
            />
          )}
          <h2 className="text-xl text-center md:text-2xl mb-3 font-semibold text-gray-900">
            {merchant.name}
          </h2>
        </div>

        {/* Cart Items */}
        <div className="mb-6">
          <List>
            <div className="space-y-4">
              {orderItems.map((item) => (
                <ListItem
                  key={item.id}
                  className="items-center"
                  icon={
                    <div className="w-10 h-10 bg-[#F4F6F9] rounded-lg flex items-center justify-center">
                      <CartItemIcon />
                    </div>
                  }
                  title={
                    <span className="text-sm font-medium text-gray-900">
                      {item.name}
                    </span>
                  }
                  right={
                    <MoneyAmount
                      amount={item.totalAmount.amount}
                      currency={item.totalAmount.currency}
                      className="text-sm font-semibold text-gray-900"
                      language={i18n.language}
                    />
                  }
                >
                  <span className="text-xs text-[#585B7A]">
                    {item.quantity}{" "}
                    {t(
                      item.quantity === 1 ? "cart.item_one" : "cart.item_other",
                    )}
                  </span>
                </ListItem>
              ))}
            </div>
          </List>

          {/* Subtotal */}
          <div className="border-t border-gray-200 pt-4 mt-6">
            <div className="flex items-center justify-between">
              <span className="text-base font-medium text-gray-900">
                {t("cart.subtotal")}
              </span>
              <MoneyAmount
                amount={subtotal}
                currency={currency}
                className="text-base font-semibold text-gray-900"
                language={i18n.language}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
